/**
 * Tiny clipboard-data parser for the import grid.
 *
 *   Excel / Google Sheets → copies as TSV (tab-delimited, no quoting,
 *                            CRLF or LF row separators)
 *   Numbers / many CSV files / hand-typed → comma-delimited, often
 *                            with quoted strings to escape commas
 *
 * We auto-detect the delimiter by counting tabs vs commas in the first
 * non-empty line: more tabs ⇒ TSV, otherwise CSV. (TSV wins ties since
 * it's the more common spreadsheet paste source.)
 *
 * The CSV path is quote-aware (RFC-4180-ish): a field wrapped in
 * double quotes can contain commas and embedded `""` for a literal
 * quote. Newlines inside quoted fields are NOT supported — keep the
 * parser small. If you need that, switch to PapaParse.
 *
 * Returns a 2-D array of strings. Empty lines at the end are dropped.
 */

export function parseClipboardData(text: string): string[][] {
  if (!text) return [];
  // Normalise line endings; CRLF from Windows, CR from old Mac.
  const normalised = text.replace(/\r\n?/g, "\n");
  // Drop a single trailing newline (common from clipboard copies).
  const trimmed = normalised.endsWith("\n")
    ? normalised.slice(0, -1)
    : normalised;
  if (trimmed === "") return [];

  const firstLine = trimmed.split("\n", 1)[0]!;
  const tabs = (firstLine.match(/\t/g) ?? []).length;
  const commas = (firstLine.match(/,/g) ?? []).length;

  // Tabs win ties — Excel paste is the dominant source.
  const useTabs = tabs >= commas;
  if (useTabs) {
    return trimmed.split("\n").map((line) => line.split("\t"));
  }
  return parseCsv(trimmed);
}

/**
 * Quote-aware CSV split. Handles "quoted, value", "embedded ""quotes""",
 * and bare unquoted fields. Doesn't try to handle newlines inside
 * quoted fields — those would have been stripped by the .split("\n")
 * before we get here, so this is a deliberate cap.
 */
function parseCsv(text: string): string[][] {
  return text.split("\n").map((line) => parseCsvLine(line));
}

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        // "" inside a quoted field = literal "
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        out.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
  }
  out.push(cur);
  return out;
}
